import React, { useEffect, useState } from "react";
import moment from "moment";
import { motion, AnimatePresence } from "framer-motion";
import { MdOutlineLocationOn, MdOutlineAccessTime, MdClose } from "react-icons/md";
import { ThreeDots } from "react-loader-spinner";
import BackButton from "../../Components/BackButton";
import { getEvents } from "../../utils/network/Crud";
import useTranslation from "../../utils/i18n/useTranslation";
import styles from "./Events.module.scss";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

const staggerContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(null);
  const { t } = useTranslation();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const result = await getEvents();
      setEvents(result?.data || []);
      setLoading(false);
    };
    load();
  }, []);

  const getDate = (event) => (event.date?.toDate ? event.date.toDate() : event.date);
  const now = new Date();
  const upcoming = events.filter((e) => getDate(e) && getDate(e) >= now);
  const past = events.filter((e) => !getDate(e) || getDate(e) < now);

  const renderCard = (event) => {
    const dateVal = getDate(event);
    return (
      <motion.div className={styles.eventCard} key={event.id} variants={fadeUp}>
        {event.image && (
          <div
            className={styles.eventImage}
            onClick={() => setActiveImage(event.image)}
            role="button"
            tabIndex={0}
          >
            <img src={event.image} alt={event.title} />
          </div>
        )}
        <div className={styles.eventBody}>
          {dateVal && (
            <div className={styles.dateBadge}>
              <span>{moment(dateVal).format("DD")}</span>
              <small>{moment(dateVal).format("MMM")}</small>
            </div>
          )}
          <h3>{event.title}</h3>
          {event.description && <p>{event.description}</p>}
          <div className={styles.metaRow}>
            {event.time && (
              <span>
                <MdOutlineAccessTime /> {event.time}
              </span>
            )}
            {event.location && (
              <span>
                <MdOutlineLocationOn /> {event.location}
              </span>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  if (loading) {
    return (
      <div className={styles.loaderContainer}>
        <ThreeDots visible height="80" width="80" color="rgb(251,157,71)" radius="9" />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <BackButton title={t("backEvents")} />
      {events.length === 0 ? (
        <div className={styles.empty}>{t("eventsEmpty")}</div>
      ) : (
        <>
          {upcoming.length > 0 && (
            <motion.section
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.15 }}
              variants={staggerContainer}
            >
              <h2 className={styles.sectionTitle}>{t("eventsUpcoming")}</h2>
              <motion.div className={styles.eventGrid} variants={staggerContainer}>
                {upcoming.map(renderCard)}
              </motion.div>
            </motion.section>
          )}
          {past.length > 0 && (
            <motion.section
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.15 }}
              variants={staggerContainer}
            >
              <h2 className={styles.sectionTitle}>{t("eventsPast")}</h2>
              <motion.div className={styles.eventGrid} variants={staggerContainer}>
                {past.map(renderCard)}
              </motion.div>
            </motion.section>
          )}
        </>
      )}

      <AnimatePresence>
        {activeImage && (
          <motion.div
            className={styles.lightbox}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActiveImage(null)}
          >
            <motion.button
              className={styles.lightboxClose}
              onClick={() => setActiveImage(null)}
              aria-label={t("close")}
            >
              <MdClose />
            </motion.button>
            <motion.img
              src={activeImage}
              alt="Preview"
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.85, opacity: 0 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Events;
